// src/dy-core.ts
function unwrap(res) {
  const choice = res.choices?.[0];
  const data = choice?.variations?.[0]?.payload?.data;
  if (!data) return null;
  return { ...data, decisionId: choice.decisionId ?? null };
}
var DEFAULT_STATE = {
  filters: {},
  ranges: {},
  page: 0,
  pageSize: 24
};
var TRIMMED_FIELDS = ["url", "group_id"];
function handleFromUrl(url) {
  const m = url.match(/\/products\/([^/?#]+)/);
  return m ? m[1] ?? null : null;
}
function cardUrl(handle, collectionHandle) {
  return collectionHandle ? `/collections/${collectionHandle}/products/${handle}?view=card` : `/products/${handle}?view=card`;
}
var DYClient = class {
  constructor(config) {
    this.config = config;
  }
  /** Serving host for the configured data centre. */
  get host() {
    const dc = this.config.dataCenter === "eu" ? "eu" : "com";
    return `https://direct.dy-api.${dc}`;
  }
  /** Collect host for the configured data centre — engagement/event reporting only. */
  get collectHost() {
    const dc = this.config.dataCenter === "eu" ? "eu" : "com";
    return `https://direct-collect.dy-api.${dc}`;
  }
  /** Experience Search endpoint — search box only (rejects any non-search selector). */
  get searchEndpoint() {
    return this.config.endpoint ?? `${this.host}/v2/serve/user/search`;
  }
  /** Browse / Category Pages endpoint — the PLP mechanism (no selector). */
  get browseEndpoint() {
    return `${this.host}/v2/serve/user/browse`;
  }
  /** Choose endpoint — arbitrary recommendation campaigns (e.g. cart cross-sell). */
  get chooseEndpoint() {
    return `${this.host}/v2/serve/user/choose`;
  }
  // ── Three first-class surfaces (same engine, different input + render) ─────
  /** Search results page — typed query via Experience Search, `?view=card` render. */
  search(text, overrides = {}) {
    return this.serve(this.searchEndpoint, this.buildSearchRequest({ ...DEFAULT_STATE, text, ...overrides }), "search");
  }
  /** Collection / PLP — DY Category Pages via the Browse endpoint. Alias for {@link browse}. */
  collection(category, overrides = {}) {
    return this.browse(category, overrides);
  }
  /**
   * Browse / Category Pages (PLP). The category is reported as page context
   * (`context.page.data`); facet/range selections go through `state.filters`/`state.ranges`
   * → `query.filters`. Field names MUST match the feed's facet columns exactly (e.g. the
   * plural `categories`) or DY silently ignores them (see {@link buildFilters}).
   */
  browse(category, overrides = {}) {
    return this.serve(this.browseEndpoint, this.buildBrowseRequest({ ...DEFAULT_STATE, category, ...overrides }), "browse");
  }
  /** Autocomplete — as-you-type, small page, rendered from JSON (no per-card fetch). */
  autocomplete(text, size = 6) {
    return this.serve(this.searchEndpoint, this.buildSearchRequest({ ...DEFAULT_STATE, text, pageSize: size }), "search");
  }
  /**
   * Recommendations via a named DY campaign (Choose API) — e.g. cart cross-sell. Unlike
   * search/browse, the campaign is merchant-configured in the DY dashboard, so the caller
   * supplies `selectorNames` (one or more campaign names) and the page context directly.
   */
  choose(selectorNames, pageType, pageData = [], overrides = {}) {
    return this.serve(this.chooseEndpoint, this.buildChooseRequest({ ...DEFAULT_STATE, ...overrides }, selectorNames, pageType, pageData), "choose");
  }
  /** POST a prepared body to a serving endpoint and return the flattened result. */
  async serve(endpoint, body, label) {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "DY-API-Key": this.config.apiKey
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      let detail = "";
      try {
        const err = await res.json();
        detail = err.error?.message ? `: ${err.error.message}` : "";
      } catch {
      }
      throw new Error(`DY ${label} failed (${res.status})${detail}`);
    }
    const json = await res.json();
    if (json.warnings?.length && typeof console !== "undefined") {
      for (const w of json.warnings) console.warn(`[dy-core] ${label} warning: ${w.message}`);
    }
    return unwrap(json);
  }
  /**
   * Fire the mandatory engagement call on card click (purchase attribution / ROI).
   *
   * Posts to the collect host (direct-collect.dy-api.*, NOT the serve host) — the engagement
   * endpoint only exists there and rejects query-param auth (401), so the DY-API-Key header is
   * mandatory. That header forces a CORS preflight, but `keepalive: true` puts the request in a
   * fetch group that outlives the document: the browser carries the whole request (preflight +
   * POST) to completion even when the card click navigates away in the same tick. Verified live
   * (cold CORS cache, synchronous navigation → 204). Body is tiny (identity + a single engagement)
   * and carries NO `context` object: for e-commerce the engagement endpoint requires only
   * `user` + `session.dy` + `engagements` — `context` is QSR-only (see dy.dev/reference/track-engagement),
   * unlike the `/serve` calls which 422 without `context.page.location`. Fire-and-forget: the
   * opaque timing means we never read the response.
   *
   * Takes only `slot` — the former second `decisionId` arg was dropped because the slotId blob
   * already encodes the decision. Some callers (dy-plp.js, dy-search.js) still pass a legacy
   * `decisionId` second argument; JS discards it, so those sites are harmless and were left as-is
   * rather than churned here — clean them up when those files are next touched on their own branch.
   */
  reportSlotClick(slot) {
    const endpoint = `${this.collectHost}/v2/collect/user/engagement`;
    const body = {
      ...readDYIdentity(),
      engagements: [{ type: "SLOT_CLICK", slotId: slot.slotId }]
    };
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", "DY-API-Key": this.config.apiKey },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => {
    });
  }
  /**
   * Experience Search request body (LIVE-VALIDATED, Mimco AU 1 & 7 Jul 2026). selector.name is
   * the fixed type "Semantic Search" (NOT a campaign name); context.page.location must be on the
   * configured store domain. `query` carries { text, pagination } plus (7 Jul 2026) optional
   * `filters` (same `{field,values}`/`{field,min,max}` shape as Browse — proven to coexist with
   * sort on this endpoint) and `sortBy` (see {@link buildSortBy}). Identity comes from window.DY.
   */
  buildSearchRequest(state) {
    const term = state.text ?? state.category ?? "";
    const location = state.location ?? `${this.config.storeUrl}/search?q=${encodeURIComponent(term)}`;
    const query = {
      text: term,
      pagination: { numItems: state.pageSize, offset: state.page * state.pageSize }
    };
    const filters = this.buildFilters(state);
    if (filters.length > 0) query.filters = filters;
    const sortBy = this.buildSortBy(state);
    if (sortBy) query.sortBy = sortBy;
    return {
      ...readDYIdentity(),
      context: {
        channel: "WEB",
        page: {
          type: "OTHER",
          data: [],
          location,
          referrer: detectReferrer(this.config.storeUrl),
          locale: this.config.locale ?? "en_AU"
        },
        device: buildDevice()
      },
      query,
      selector: { name: this.config.selectorName ?? "Semantic Search" },
      options: this.buildOptions(state)
    };
  }
  /**
   * Browse / Category Pages request body (LIVE-VALIDATED, Mimco AU 1 Jul 2026). Rules proven
   * against the live API: NO `selector` (browse rejects it); the category is page context
   * (`context.page.type: "CATEGORY"`, `data: [category]`); `query` carries `filters` + `pagination`,
   * plus `sortBy` when a non-relevance sort is active (`query.category`/`query.categories` are
   * rejected). Identity comes from window.DY.
   */
  buildBrowseRequest(state) {
    const location = state.location ?? `${this.config.storeUrl}/`;
    const query = {
      filters: this.buildFilters(state),
      pagination: { numItems: state.pageSize, offset: state.page * state.pageSize }
    };
    const sortBy = this.buildSortBy(state);
    if (sortBy) query.sortBy = sortBy;
    return {
      ...readDYIdentity(),
      context: {
        channel: "WEB",
        page: {
          type: "CATEGORY",
          data: state.category ? [state.category] : [],
          location,
          referrer: detectReferrer(this.config.storeUrl),
          locale: this.config.locale ?? "en_AU"
        },
        device: buildDevice()
      },
      query,
      options: this.buildOptions(state)
    };
  }
  /**
   * Choose API request body (LIVE-VALIDATED, Mimco AU 13 Jul 2026, host `direct.dy-api.com` —
   * the older `direct-{region}.dynamicyield.com` hosts are stale/unresolvable). `context.page.location`
   * is REQUIRED (omitting it → 422 "must contain context page location"); selector uses the
   * PLURAL `names` array (unlike Search's singular `name`). Identity comes from window.DY.
   */
  buildChooseRequest(state, selectorNames, pageType, pageData) {
    const location = state.location ?? `${this.config.storeUrl}/`;
    const request = {
      ...readDYIdentity(),
      context: {
        channel: "WEB",
        page: {
          type: pageType,
          data: pageData,
          location,
          referrer: detectReferrer(this.config.storeUrl),
          locale: this.config.locale ?? "en_AU"
        },
        device: buildDevice()
      },
      selector: { names: Array.isArray(selectorNames) ? selectorNames : [selectorNames] },
      options: this.buildOptions(state)
    };
    if (state.realtimeRules && state.realtimeRules.length > 0) {
      request.realtimeRules = state.realtimeRules;
    }
    return request;
  }
  /**
   * Shared serve `options`. Adds `productData.fieldFilter` when a field allow-list is set (per-call
   * `state.fields` wins over the client-level `config.fields` default) so DY returns only those
   * `productData` columns — a lighter payload. See {@link DYClientConfig.fields} for the caveat
   * that field names must be real feed columns (no `handle`).
   */
  buildOptions(state) {
    const options = { returnAnalyticsMetadata: true };
    const fields = state.fields ?? this.config.fields ?? (this.config.trimmed ? TRIMMED_FIELDS : void 0);
    if (fields && fields.length > 0) {
      options.productData = { fieldFilter: fields };
    }
    return options;
  }
  /**
   * Map query state → DY Browse `query.filters[]`: term filters `{ field, values }` and range
   * filters `{ field, min, max }`. The `field` MUST be the exact feed facet column name (e.g. the
   * PLURAL `categories`, `price`, `in_stock`) — a wrong/unknown column is silently ignored by DY
   * (returns everything) with only a `warnings[]` entry, so callers must use real facet columns.
   */
  buildFilters(state) {
    const filters = [];
    for (const [field, values] of Object.entries(state.filters)) {
      if (values.length > 0) filters.push({ field, values });
    }
    for (const [field, [min, max]] of Object.entries(state.ranges)) {
      filters.push({ field, min, max });
    }
    if (this.config.inStockOnly) filters.push({ field: "in_stock", values: ["true"] });
    // Per-market availability hard-filter: the SSR-detected market picks the feed's boolean
    // `product_live_{au|nz}` column so DY never serves a product excluded from the shopper's
    // market catalogue. Field is empty for markets without a column (only au/nz exist), in which
    // case no filter is added and the full set serves — same as before this flag existed.
    if (this.config.availabilityField) filters.push({ field: this.config.availabilityField, values: ["true"] });
    return filters;
  }
  /**
   * Map sort state → DY `query.sortBy` for Experience Search. `state.sort` is `{ field, order }`:
   * `field` is a BARE feed column (`price`, `name`, `publish_time`) or the special `"popularity"`
   * (engagement rank); `order` is "asc"|"desc" (defaults to "asc"). Returns undefined for the
   * default relevance ranking (sortBy omitted so DY keeps its semantic search_score order).
   * GOTCHA (live-probed 7 Jul 2026): the docs' typed `type:number:<col>` form is rejected by this
   * feed with warning W096 for BUILT-IN columns (e.g. `price`) — use the bare column name there.
   * EXCEPTION (live-validated 27 Aug 2026): a CUSTOM feed column declared numeric via a `type:number:`
   * NAME PREFIX (e.g. `type:number:price_au`/`price_nz`) is registered by DY under that literal
   * prefixed name, so its sort/filter `field` MUST use the full prefixed name — the bare name is
   * silently ignored. So dy-search-facets deliberately passes `type:number:price_{market}` here; do
   * NOT "fix" that to a bare name. Called from both {@link buildSearchRequest}
   * and {@link buildBrowseRequest}: the Browse endpoint accepts the same `sortBy` shape (DY Choose
   * docs, confirmed 6 Aug 2026), with `field` a real feed column (case-sensitive) or `"popularity"`.
   */
  buildSortBy(state) {
    const sort = state.sort;
    if (!sort || !sort.field) return void 0;
    return { field: sort.field, order: sort.order === "desc" ? "desc" : "asc" };
  }
};
function detectDeviceType() {
  if (typeof window === "undefined") return "desktop";
  if (window.matchMedia?.("(max-width: 749px)").matches) return "smartphone";
  if (window.matchMedia?.("(max-width: 1024px)").matches) return "tablet";
  return "desktop";
}
function detectReferrer(storeUrl) {
  if (typeof document !== "undefined" && document.referrer) return document.referrer;
  return `${storeUrl}/`;
}
function buildDevice() {
  const device = { type: detectDeviceType() };
  if (typeof navigator !== "undefined" && navigator.userAgent) device.userAgent = navigator.userAgent;
  return device;
}
function dyGlobal() {
  if (typeof window === "undefined") return void 0;
  return window.DY;
}
function readDYCookie(name) {
  if (typeof document === "undefined") return void 0;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  if (!match) return void 0;
  try {
    return decodeURIComponent(match[1] ?? "");
  } catch {
    return void 0;
  }
}
function readDYIdentity() {
  const dy = dyGlobal();
  const user = {};
  const session = {};
  // `_dyid` is DY's third-party cookie and can be blocked (Safari ITP / Chrome third-party
  // restrictions); `_dyid_server` is the first-party copy DY sets specifically to survive those
  // restrictions (it holds the same value). Falling back to it recovers the user id on first load —
  // before the external DY script populates `window.DY` — and whenever `_dyid` is blocked.
  const dyid = dy?.dyid ?? readDYCookie("_dyid") ?? readDYCookie("_dyid_server");
  if (dyid != null) {
    user.dyid = String(dyid);
    user.dyid_server = String(dyid);
  }
  if (typeof dy?.userActiveConsent?.accepted === "boolean") {
    user.active_consent_accepted = dy.userActiveConsent.accepted;
  }
  const jsession = dy?.jsession ?? readDYCookie("_dyjsession");
  if (jsession != null) session.dy = String(jsession);
  return { user, session };
}
export {
  DEFAULT_STATE,
  DYClient,
  TRIMMED_FIELDS,
  cardUrl,
  dyGlobal,
  handleFromUrl,
  readDYIdentity,
  unwrap
};
//# sourceMappingURL=dy-core.js.map
